import { useAuthStore } from "@/stores/auth";
import { useErrorStore } from "@/stores/errorStore";

export function getApiBase(): string {
  if (typeof window === "undefined") return "";
  if ((window as any).__API_BASE__) return (window as any).__API_BASE__;
  const saved = localStorage.getItem("cpa_api_base");
  if (saved) return saved;
  if (window.location.hostname === "124.222.192.130" || window.location.hostname === "cpabuddy.cn") return "";
  return import.meta.env.VITE_API_BASE || "";
}

interface ApiFetchOptions {
  method?: string;
  body?: any;
  auth?: boolean;
  headers?: Record<string, string>;
  /** 静默：不自动推送到全局错误横幅（业务侧自行处理） */
  silent?: boolean;
}

/**
 * 增强的 API 错误：携带 HTTP 状态、错误码、请求 ID、堆栈
 */
export class ApiError extends Error {
  status?: number;
  code?: string;
  requestId?: string;
  detail?: any;
  path: string;
  constructor(opts: { message: string; status?: number; code?: string; requestId?: string; detail?: any; path: string }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.requestId = opts.requestId;
    this.detail = opts.detail;
    this.path = opts.path;
  }
}

/**
 * HTTP 状态 → 友好说明
 */
function statusHint(status?: number, code?: string): string | undefined {
  if (code === "ai_overloaded") return "AI 模型服务当前繁忙，请稍后再试或选择非高峰时段使用。";
  if (status === 401) return "登录状态已过期，请重新登录后再试。";
  if (status === 403) return "当前账号没有访问该资源的权限。如果是管理员操作请用 leon 账号登录。";
  if (status === 404) return "请求的资源不存在，可能是接口路径变更或资源已删除。";
  if (status === 422) return "请求参数不符合后端校验，请检查输入是否完整、类型是否正确。";
  if (status === 429) return "请求过于频繁，已被限流。请稍候再试。";
  if (status === 500) return "服务器内部错误，通常是后端代码 bug 或数据库问题。请截图报错联系管理员排查。";
  if (status === 502 || status === 503 || status === 504) return "服务暂不可用，可能是部署中或网关问题。请稍候再试。";
  if (code === "TOKEN_INSUFFICIENT") return "账户余额不足，请联系管理员充值后再试。";
  if (code === "RATE_LIMITED") return "已达速率上限，请稍候再试。";
  return undefined;
}

async function handle401<T>(res: Response, retryFn: () => Promise<T>): Promise<T> {
  if (res.status !== 401) throw new Error(`HTTP ${res.status}`);
  const auth = useAuthStore();
  try {
    await auth.refreshToken();
    return retryFn();
  } catch {
    auth.logout();
    window.location.href = "/auth/login";
    throw new ApiError({ message: "登录已过期", status: 401, path: "" });
  }
}

export async function apiFetch<T = any>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };
  const legacyToken = localStorage.getItem("cpabuddy_token");
  if (options.auth && legacyToken) {
    headers["Authorization"] = `Bearer ${legacyToken}`;
  }

  const doFetch = async (): Promise<T> => {
    let res: Response;
    try {
      res = await fetch(`${getApiBase()}${path}`, {
        method: options.method || "GET",
        headers,
        body: isFormData ? options.body : (options.body ? JSON.stringify(options.body) : undefined),
        credentials: "include",
      });
    } catch (networkErr: any) {
      // 网络层失败（断网/CORS/DNS）
      const err = new ApiError({
        message: `网络错误：${networkErr?.message || "无法连接到服务器"}`,
        path,
      });
      if (!options.silent) pushToStore(err, path);
      throw err;
    }

    if (res.status === 401) {
      return handle401(res, doFetch);
    }

    const data: any = await res.json().catch(() => null);
    if (!res.ok) {
      const err = new ApiError({
        message: data?.message || data?.detail || `HTTP ${res.status}`,
        status: res.status,
        code: data?.code,
        requestId: data?.requestId,
        detail: data?.detail,
        path,
      });
      if (!options.silent) pushToStore(err, path);
      throw err;
    }
    return data;
  };

  return doFetch();
}

function pushToStore(err: ApiError, path: string) {
  try {
    const store = useErrorStore();
    store.fromUnknown(err, {
      title: defaultTitle(err.status, err.code),
      source: path,
      suggestion: statusHint(err.status, err.code),
    });
  } catch {
    // store 未初始化（极早阶段）忽略
  }
}

function defaultTitle(status?: number, _code?: string): string {
  if (status === 401) return "登录已过期";
  if (status === 403) return "没有权限";
  if (status === 404) return "资源不存在";
  if (status === 422) return "请求参数有误";
  if (status === 429) return "请求过于频繁";
  if (status === 500) return "服务器内部错误";
  if (status === 502 || status === 503 || status === 504) return "服务暂不可用";
  if (status === 401) return "登录已过期";
  if (typeof status === "number") return `请求失败（HTTP ${status}）`;
  return "操作失败";
}

export async function downloadFile(path: string, filename?: string) {
  const doFetch = async (): Promise<void> => {
    const legacyToken = localStorage.getItem("cpabuddy_token");
    const res = await fetch(`${getApiBase()}${path}`, {
      headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {},
      credentials: "include",
    });
    if (res.status === 401) {
      const auth = useAuthStore();
      try {
        await auth.refreshToken();
        return doFetch();
      } catch {
        auth.logout();
        window.location.href = "/auth/login";
        throw new Error("登录已过期");
      }
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      const err = new ApiError({ message: text || `HTTP ${res.status}`, status: res.status, path });
      try { useErrorStore().fromUnknown(err, { source: path }); } catch {}
      throw err;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  return doFetch();
}

export async function financeApiFetch<T = any>(path: string, options: Omit<ApiFetchOptions, "auth"> = {}): Promise<T> {
  const data = await apiFetch<any>(path, { ...options, auth: true });
  if (data && typeof data === "object" && "ok" in data) {
    if (data.ok === true && "data" in data) {
      return data.data as T;
    }
    if (data.ok === false && "error" in data) {
      const e = data.error;
      throw new ApiError({
        message: e?.message || e?.code || "请求失败",
        code: e?.code,
        detail: e,
        path,
      });
    }
  }
  return data as T;
}

export function getToken(): string | null {
  return localStorage.getItem("cpabuddy_token");
}

export function getUser() {
  try {
    const raw = localStorage.getItem("cpabuddy_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isAdmin(): boolean {
  const user = getUser();
  return user?.role === "admin" || user?.username === "leon";
}

export function logout() {
  const auth = useAuthStore();
  auth.logout();
  window.location.href = "/auth/login";
}